import {useCallback, useContext} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {AppContext} from '../contexts/AppContextProvider'
import {NavigateOptions} from "react-router/lib/hooks";
import {ComplaintState} from "../contexts/types";

export const screenPaths = {
    guestForm: '/guestform',
    natureOfComplaint: '/',
    natureOfComplaintGuest: '/guest',
    productSearch: '/productsearch',
    productType: '/producttype',
    eventOccurrence: '/eventoccurrence',
    treatmentDates: '/treatmentdates',
    patientInfo: '/patientinfo',
    complaintType: '/complainttype',
    clinicalCondition: '/clinicalcondition',
    abutmentsInformation: '/abutmentsinformation',
    instrumentInformation: '/instrumentinformation',
    secondaryDevice: '/secondarydevice',
    availableToReturn: '/availabletoreturn',
    shippingInfo: '/shippinginfo',
    complaintCompletion: '/complaintcompletion',
} as const

type ScreenName = keyof typeof screenPaths
type ScreenPaths = Record<ScreenName, string>
type ScreenConditions = Record<ScreenName, (complaint: ComplaintState, loggedIn: boolean) => boolean>
type ScreenEntry = [ScreenName, string]
type ScreenEntries = ScreenEntry[]

export const screenConditions: ScreenConditions = {
    guestForm: ({metadata: {complaintWithCustomerData}}) => !complaintWithCustomerData,
    natureOfComplaint: ({metadata: {isInitialComplaint}}, loggedIn) => loggedIn && isInitialComplaint,
    natureOfComplaintGuest: ({metadata: {isInitialComplaint}}, loggedIn) => !loggedIn && isInitialComplaint,
    productSearch: ({metadata: {complaintOnSecondaryDevice}}) => !complaintOnSecondaryDevice,
    productType: () => true,
    eventOccurrence: ({metadata: {complaintOnSecondaryDevice}}) => !complaintOnSecondaryDevice,
    treatmentDates: (c) => (c.generalComplaint.wasProductPlaced === 'YES' || c.complaintType.location === '5_CLINIC_AFTER_PROCEDURE')
        && !c.metadata.complaintWithPatientAndTreatmentData,
    patientInfo: (c) => c.generalComplaint.wasPatientInvolved === 'YES',
    complaintType: () => true,
    clinicalCondition: (c) => c.generalComplaint.wasPatientInvolved === 'YES',
    abutmentsInformation: (c) => c.generalComplaint.productType === 'ABUTMENT' && !c.metadata.complaintOnSecondaryDevice,
    instrumentInformation: ({generalComplaint: {productType}}) => productType === 'LAB_INSTRUMENT_LAB_SCREW' || productType === 'CLINICAL_INSTRUMENT',
    secondaryDevice: ({metadata: {complaintOnSecondaryDevice}}) => !complaintOnSecondaryDevice,
    availableToReturn: () => true,
    shippingInfo: () => true,
    complaintCompletion: () => true,
}

const useScreenFlow = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const {complaint, loggedIn} = useContext(AppContext)

    const makeEntriesArrayFromScreenPaths = useCallback((screenPaths: ScreenPaths): ScreenEntries => Object.entries(screenPaths) as ScreenEntries, [])
    const testEntryCondition = useCallback((entry: ScreenEntry) => screenConditions[entry[0]](complaint, loggedIn), [complaint, loggedIn])
    const getAdjustedArray = useCallback((entriesArray: ScreenEntries): ScreenEntries => [...entriesArray].filter(testEntryCondition), [testEntryCondition])
    const getProgressRate = useCallback((numberOfScreens: number): number => 100 / (numberOfScreens - 1), [])
    const getScreenIndex = useCallback((entriesArray: ScreenEntries, pathname: string): number => entriesArray.findIndex((entry) => entry[1] === pathname), [])

    const calculateProgress = useCallback((): number => {
        const entriesArray: ScreenEntries = makeEntriesArrayFromScreenPaths(screenPaths)
        const adjustedArray = getAdjustedArray(entriesArray)
        const numberOfScreens = adjustedArray.length
        const progressRate = getProgressRate(numberOfScreens)
        const screenIndex = getScreenIndex(adjustedArray, location.pathname)

        return screenIndex * progressRate
    }, [getAdjustedArray, getProgressRate, getScreenIndex, location.pathname, makeEntriesArrayFromScreenPaths])

    const getNextActivePath = useCallback((entriesArray: ScreenEntries, currentScreenIndex: number, direction: 'forward' | 'backward'): string => {
        let isConditionMet = false
        let i = 0
        let entryIndex = currentScreenIndex
        let entry = entriesArray[entryIndex]

        while (!isConditionMet) {
            i++

            entryIndex = direction === 'forward'
                ? currentScreenIndex + i
                : currentScreenIndex - i

            entry = entriesArray[entryIndex]

            isConditionMet = testEntryCondition(entry)
        }

        return entry[1]
    }, [testEntryCondition])

    const navigateToScreen = useCallback((direction: 'forward' | 'backward') => {
        const entriesArray = makeEntriesArrayFromScreenPaths(screenPaths)
        const currentScreenIndex = getScreenIndex(entriesArray, location.pathname)
        const nextScreenPath = getNextActivePath(entriesArray, currentScreenIndex, direction)
        const history = JSON.parse(sessionStorage.getItem('history') || '["/"]')

        if (direction === 'forward') history.push(nextScreenPath)
        else history.pop()

        navigate(nextScreenPath)

        sessionStorage.setItem('currentPage', nextScreenPath)
        sessionStorage.setItem('history', JSON.stringify(history))
    }, [makeEntriesArrayFromScreenPaths, getScreenIndex, getNextActivePath, navigate, location.pathname])

    const navigateForward = useCallback(() => navigateToScreen('forward'), [navigateToScreen])
    const navigateBackward = useCallback(() => navigateToScreen('backward'), [navigateToScreen])
    const navigateTo = useCallback((pathname: string, params?: NavigateOptions) => {
        if (params?.replace) {
            navigate(pathname, params)
            sessionStorage.setItem('history', `["${pathname}"]`)
            return
        }

        navigate(pathname)
        const history = JSON.parse(sessionStorage.getItem('history') || '["/"]')
        history.push(pathname)
        sessionStorage.setItem('history', JSON.stringify(history))
    }, [navigate])

    return {
        screenPaths,
        calculateProgress,
        navigateForward,
        navigateBackward,
        navigateTo,
    }
}

export default useScreenFlow
